import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { firstValueFrom, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import {
  AddCareerRoleProfileSkillTopicsRequest,
  CareerRoleProfileSkillMutationResponse
} from '../../../../models';
import { CareerRoleAdminActionsService } from './career-role-admin-actions.service';

export interface TopicDependencyOption {
  skillId: string;
  skillName: string;
  topicName: string;
  label: string;
}

export interface AddProfileTopicDialogData {
  roleSlug: string;
  level: string;
  skillId: string;
  skillName: string;
  specializationSlug?: string;
  availableTopics?: TopicDependencyOption[];
}

@Component({
  selector: 'app-add-profile-topic-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressBarModule,
    MatSelectModule,
    MatIconModule
  ],
  template: `
    <h2 mat-dialog-title>Add topics to {{ data.skillName }}</h2>
    <mat-progress-bar *ngIf="saving" mode="indeterminate"></mat-progress-bar>
    <form [formGroup]="form" (ngSubmit)="submit()">
      <mat-dialog-content class="content">
        <div formArrayName="topics">
          <div class="topic-block" *ngFor="let topic of topics.controls; let i = index" [formGroupName]="i">
            <div class="topic-row">
              <mat-form-field appearance="outline" class="topic-name">
                <mat-label>Topic</mat-label>
                <input matInput formControlName="name" />
              </mat-form-field>
              <mat-form-field appearance="outline" class="topic-prof">
                <mat-label>Proficiency</mat-label>
                <mat-select formControlName="requiredProficiency">
                  <mat-option value="beginner">beginner</mat-option>
                  <mat-option value="intermediate">intermediate</mat-option>
                  <mat-option value="advanced">advanced</mat-option>
                  <mat-option value="expert">expert</mat-option>
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline" class="topic-imp">
                <mat-label>Importance</mat-label>
                <input matInput type="number" min="0" max="1" step="0.05" formControlName="importance" />
              </mat-form-field>
              <button mat-icon-button type="button" (click)="removeTopic(i)" [disabled]="topics.length === 1">
                <mat-icon>close</mat-icon>
              </button>
            </div>
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Depends on topics</mat-label>
              <mat-select formControlName="dependsOnKeys" multiple>
                <mat-option *ngFor="let opt of topicOptions" [value]="opt.label">{{ opt.label }}</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </div>
        <button mat-stroked-button type="button" (click)="addTopic()">Add topic</button>
        <p class="error" *ngIf="error">{{ error }}</p>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button type="button" (click)="close()" [disabled]="saving">Cancel</button>
        <button mat-flat-button color="primary" type="submit" [disabled]="form.invalid || saving">Save</button>
      </mat-dialog-actions>
    </form>
  `,
  styles: [
    `
      .content { min-width: 480px; }
      .topic-block { margin-bottom: 0.75rem; padding-bottom: 0.5rem; border-bottom: 1px solid #e2e8f0; }
      .topic-row { display: flex; gap: 0.5rem; align-items: flex-start; flex-wrap: wrap; }
      .topic-name { flex: 1; min-width: 140px; }
      .topic-prof { width: 140px; }
      .topic-imp { width: 110px; }
      .full-width { width: 100%; }
      .error { color: #b00020; }
    `
  ]
})
export class AddProfileTopicDialogComponent implements OnDestroy {
  private readonly api = inject(CsrWebApiService);
  private readonly actions = inject(CareerRoleAdminActionsService);
  private readonly dialogRef = inject(MatDialogRef<AddProfileTopicDialogComponent, CareerRoleProfileSkillMutationResponse | undefined>);
  readonly data = inject<AddProfileTopicDialogData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private topicsSubscription?: Subscription;

  saving = false;
  error = '';
  /** Stable reference for mat-select (avoid `?? []` in the template). */
  topicOptions: TopicDependencyOption[] = [...(this.data.availableTopics ?? [])];
  private readonly profileTopicOptions: TopicDependencyOption[] = [...(this.data.availableTopics ?? [])];

  readonly form = this.fb.nonNullable.group({
    topics: this.fb.array([this.createTopicGroup()])
  });

  constructor() {
    this.topicsSubscription = this.topics.valueChanges
      .pipe(
        debounceTime(100),
        map(() =>
          this.topics
            .getRawValue()
            .map((t) => ((t.name as string | undefined)?.trim() ?? ''))
            .join('\u0001')
        ),
        distinctUntilChanged()
      )
      .subscribe(() => this.refreshTopicDependencyOptions());
  }

  ngOnDestroy(): void {
    this.topicsSubscription?.unsubscribe();
  }

  get topics(): FormArray {
    return this.form.get('topics') as FormArray;
  }

  addTopic(): void {
    this.topics.push(this.createTopicGroup());
    this.refreshTopicDependencyOptions();
  }

  removeTopic(index: number): void {
    if (this.topics.length > 1) {
      this.topics.removeAt(index);
      this.refreshTopicDependencyOptions();
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.saving) {
      return;
    }
    this.saving = true;
    this.error = '';
    const request: AddCareerRoleProfileSkillTopicsRequest = {
      topics: this.topics.getRawValue().map((t) => {
        const keys = (t.dependsOnKeys as string[]) ?? [];
        const topicName = (t.name as string).trim();
        return {
          name: topicName,
          requiredProficiency: t.requiredProficiency as string,
          importance: Number(t.importance ?? 0.5),
          dependsOn: keys
            .map((key) => this.topicOptions.find((o) => o.label === key))
            .filter((o): o is TopicDependencyOption => !!o && o.topicName !== topicName)
            .map((o) => ({
              skillId: o.skillId === this.data.skillId ? null : o.skillId,
              topicName: o.topicName,
              kind: 'hard'
            }))
        };
      })
    };
    try {
      const result = this.data.specializationSlug
        ? await firstValueFrom(
            this.api.addCareerRoleFocusSkillTopics(
              this.data.roleSlug,
              this.data.specializationSlug,
              this.data.level,
              this.data.skillId,
              request
            )
          )
        : await firstValueFrom(
            this.api.addCareerRoleCoreSkillTopics(
              this.data.roleSlug,
              this.data.level,
              this.data.skillId,
              request
            )
          );
      this.dialogRef.close(result);
    } catch (error) {
      this.error = this.actions.errorMessage(error);
    } finally {
      this.saving = false;
    }
  }

  private createTopicGroup() {
    return this.fb.nonNullable.group({
      name: ['', [Validators.required, Validators.minLength(1)]],
      requiredProficiency: ['intermediate', Validators.required],
      importance: [0.5, [Validators.required, Validators.min(0), Validators.max(1)]],
      dependsOnKeys: this.fb.nonNullable.control<string[]>([])
    });
  }

  /** Include unsaved topic names from this dialog so depends-on can reference them. */
  private refreshTopicDependencyOptions(): void {
    const skillName = this.data.skillName;
    const skillId = this.data.skillId;
    const options = [...this.profileTopicOptions];
    const seen = new Set(options.map((o) => o.label));
    for (const row of this.topics.getRawValue()) {
      const name = (row.name as string | undefined)?.trim() ?? '';
      if (!name) {
        continue;
      }
      const label = `${skillName} / ${name}`;
      if (seen.has(label)) {
        continue;
      }
      seen.add(label);
      options.push({ skillId, skillName, topicName: name, label });
    }
    this.topicOptions = options;
  }
}